import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useContent } from "../lib/content";
import { useCurrency } from "../lib/currency";
import { Phone, ShoppingCart, ChevronRight, LogOut, Wrench, Flame, Store } from "lucide-react";
import { BRANDS, darkLogoFor } from "../lib/brand";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, total } = useCart();
  const { t } = useContent();
  const { currency, setCurrency, format } = useCurrency();
  const navigate = useNavigate();
  const [searchQ, setSearchQ] = useState("");

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <header data-testid="navbar">
      {/* Top utility bar */}
      <div className="topbar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone size={11} />
            <span>{t("topbar_phone_label", "HÍVJON")}: {t("topbar_phone", "+36 20 9 40 40 04")}</span>
          </div>
          <div className="hidden md:block font-bold" style={{ color: "var(--signal)" }}>
            {t("topbar_notice", "AZ ÁRLISTÁBAN SZEREPLŐ ÁRAK NETTÓ ÁRAK, AZ ÁFA-T NEM TARTALMAZZÁK.")}
          </div>
          <nav className="flex items-center gap-1 text-[11px] uppercase">
            <Link to="/catalog" className="px-2 py-1 hover:text-[color:var(--brand)] flex items-center gap-1" data-testid="nav-catalog">
              Kezdőlap
            </Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/orders" className="px-2 py-1 hover:text-[color:var(--brand)] flex items-center gap-1" data-testid="nav-orders">
              Rendeléseim
            </Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/cart" className="px-2 py-1 hover:text-[color:var(--brand)] flex items-center gap-1" data-testid="cart-button">
              Bevásárlókosár
            </Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <span className="px-2 py-1 font-bold" data-testid="cart-total-display">
              {format(total)}
              {count > 0 && <span className="ml-1" style={{ color: "var(--signal)" }} data-testid="cart-count-badge">({count})</span>}
            </span>
            {/* Currency switcher pill */}
            <span className="ml-2 inline-flex items-center rounded-full border overflow-hidden text-[10px] font-bold" style={{ borderColor: "var(--border)" }} data-testid="currency-switcher">
              <button
                type="button"
                onClick={() => setCurrency("EUR")}
                className="px-2 py-1 transition-colors"
                style={{
                  background: currency === "EUR" ? "var(--brand)" : "transparent",
                  color: currency === "EUR" ? "#fff" : "var(--text-mute)",
                }}
                data-testid="currency-eur"
                title="Árak euróban"
              >
                EUR
              </button>
              <button
                type="button"
                onClick={() => setCurrency("HUF")}
                className="px-2 py-1 transition-colors"
                style={{
                  background: currency === "HUF" ? "var(--brand)" : "transparent",
                  color: currency === "HUF" ? "#fff" : "var(--text-mute)",
                }}
                data-testid="currency-huf"
                title="Árak forintban"
              >
                Ft
              </button>
            </span>
          </nav>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex items-center justify-between gap-6">
          <Link to="/catalog" className="flex items-center gap-3" data-testid="brand-link">
            <div
              className="w-14 h-14 flex items-center justify-center rounded shrink-0"
              style={{ background: "var(--text)", color: "#fff", fontWeight: 900, fontSize: 20, letterSpacing: "-0.02em" }}
            >
              {t("header_logo_initials", "P24")}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-xl" style={{ color: "var(--text)" }}>{t("header_company_name", "prnds24.hu")}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{t("header_company_subtitle", "OEM Alkatrészek · Nagyker")}</div>
            </div>
          </Link>

          <div className="hidden lg:block flex-1 text-center px-4">
            {/* 3 lines, all the same size + weight + colour to maximise
                visual impact ("feltűnőbb") per user request. The third line
                is a brand-new CMS key — defaults to empty so it can stay
                hidden until the admin fills it in via /admin/content. */}
            <div className="text-base font-bold uppercase tracking-wide" style={{ color: "var(--signal)" }}>
              {t("header_price_period", "AZ ÁRAK 2026.04. HAVIAK!")}
            </div>
            <div className="text-base font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--signal)" }}>
              {t("header_price_subtext", "PONTOS ÁRAKÉRT KÜLDJÖN EMAIL-T,")}
            </div>
            <div className="text-base font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--signal)" }}>
              {t("header_price_subtext2", "VAGY LÉPJEN BE A FIÓKJÁBA.")}
            </div>
          </div>

          <div className="hidden md:block shrink-0" />
        </div>

        {/* Admin row (no search field here — only on /catalog) */}
        <div className="border-t" style={{ borderColor: "var(--border)" }}>
          <div className="max-w-[1200px] mx-auto px-4 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {user && (
                <span className="text-xs" style={{ color: "var(--text-mute)" }} data-testid="current-user-email">
                  Üdv, <strong style={{ color: "var(--text)" }}>{user.email}</strong>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {user?.role === "admin" && (
                <NavLink to="/admin" className="btn-ghost !py-1 !px-2 text-[11px]" data-testid="nav-admin">
                  <Wrench size={12} /> Admin
                </NavLink>
              )}
              <button onClick={handleLogout} className="btn-ghost !py-1 !px-2 text-[11px]" data-testid="logout-button">
                <LogOut size={12} /> Kilépés
              </button>
            </div>
          </div>
        </div>

        {/* Brand nav (Eurobetom style) */}
        <div className="brand-nav">
          <div className="max-w-[1200px] mx-auto px-4 flex items-center gap-1 overflow-x-auto whitespace-nowrap">
            <Link to="/catalog" className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white" style={{ color: "var(--brand)" }} data-testid="brand-nav-all">
              Összes alkatrész
            </Link>
            <Link
              to="/catalog?promo=true"
              className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white flex items-center gap-1.5"
              style={{ color: "#c8102e" }}
              data-testid="brand-nav-promo"
            >
              <Flame size={13} /> Akciók
            </Link>
            <Link
              to="/catalog?brand=piacter"
              className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white flex items-center gap-1.5"
              style={{ color: "#0A8043" }}
              data-testid="brand-nav-piacter"
            >
              <Store size={13} /> Akciós Piactér
            </Link>
            {Object.entries(BRANDS)
              // Hide the legacy combined `mercedes` brand from the top nav —
              // we now ship only the PKW / LKW variants. The combined brand
              // remains in BRANDS so old products still resolve their logo,
              // but the user-facing nav only exposes the split variants.
              .filter(([key]) => key !== "mercedes")
              .map(([key, b]) => (
              <Link
                key={key}
                to={`/catalog?brand=${key}`}
                className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white flex items-center gap-1.5"
                style={{ color: "var(--text)" }}
                data-testid={`brand-nav-${key}`}
              >
                <img src={darkLogoFor(b)} alt="" className="h-3.5 w-3.5 object-contain" />
                {b.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
